// Product-level entitlements: which top-level tool(s) a user may open
// (School Explorer / City Explorer). Independent of `role`, which governs
// capability *within* a product. Mirrors backend/app/utils/auth_helpers.py.
export const PRODUCTS = {
  SCHOOL_EXPLORER: 'school_explorer',
  CITY_EXPLORER: 'city_explorer',
};

export const ALL_PRODUCTS = [PRODUCTS.SCHOOL_EXPLORER, PRODUCTS.CITY_EXPLORER];

// Users without a `products` claim/profile field default to School Explorer
// only, which preserves today's behavior for every existing account.
export const DEFAULT_PRODUCTS = [PRODUCTS.SCHOOL_EXPLORER];

export const PRODUCT_LABELS = {
  [PRODUCTS.SCHOOL_EXPLORER]: 'School Explorer',
  [PRODUCTS.CITY_EXPLORER]: 'City Explorer',
};

/** Validate/clean a products list, falling back to the default when empty or invalid. */
export const normalizeProducts = (products) => {
  if (!Array.isArray(products)) return [...DEFAULT_PRODUCTS];
  const cleaned = products.filter((p) => ALL_PRODUCTS.includes(p));
  return cleaned.length ? cleaned : [...DEFAULT_PRODUCTS];
};

/** Admins implicitly have every product; everyone else needs it in their `products` list. */
export const hasProductAccess = (user, product) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return normalizeProducts(user.products).includes(product);
};

export const getUserProducts = (user) => {
  if (!user) return [];
  if (user.role === 'admin') return [...ALL_PRODUCTS];
  return normalizeProducts(user.products);
};

// The user's explicitly-granted products, ignoring the admin "has everything"
// bypass. Used to decide whether to show the post-login tool picker: an
// admin who hasn't been explicitly granted both products should land
// straight in School Explorer exactly as they do today, not be prompted to
// choose. Admins who *are* explicitly granted both still get the picker.
export const getExplicitProducts = (user) => (user ? normalizeProducts(user.products) : []);

export const hasMultipleExplicitProducts = (user) => getExplicitProducts(user).length > 1;

// True when this user needs to be routed through the /select-tool picker
// before landing in either product: they have more than one explicit
// product grant and haven't chosen one yet this session.
export const requiresProductChoice = (user) => hasMultipleExplicitProducts(user) && !getActiveProduct();

const SESSION_ACTIVE_PRODUCT_KEY = 'edc_active_product';

export const getActiveProduct = () => {
  try {
    return sessionStorage.getItem(SESSION_ACTIVE_PRODUCT_KEY);
  } catch {
    return null;
  }
};

export const setActiveProduct = (product) => {
  try {
    sessionStorage.setItem(SESSION_ACTIVE_PRODUCT_KEY, product);
  } catch {
    /* ignore */
  }
};

export const clearActiveProduct = () => {
  try {
    sessionStorage.removeItem(SESSION_ACTIVE_PRODUCT_KEY);
  } catch {
    /* ignore */
  }
};
